import { useMemo } from 'react';
import { Loader2, Play } from 'lucide-react';
import RevenueDashboardFilters from './RevenueDashboardFilters';
import RevenueTrendChart from './RevenueTrendChart';
import RevenueBreakdown from './RevenueBreakdown';
// import VoucherStatsCard from './VoucherStatsCard';
import { useRevenueStore } from '../../store/revenue.store';

interface Props {
  isDark: boolean;
  theme: Record<string, string>;
}

const DEAL_TYPE_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444'];
const PRODUCT_TYPE_COLORS = ['#06b6d4', '#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#ef4444', '#f97316', '#14b8a6', '#a855f7', '#6b7280'];
const PAYMENT_COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#f97316', '#14b8a6', '#a855f7', '#6b7280', '#84cc16', '#e11d48'];
const SALES_COLORS = ['#f97316', '#3b82f6', '#10b981', '#ef4444', '#8b5cf6', '#f59e0b', '#ec4899', '#06b6d4', '#14b8a6', '#a855f7', '#84cc16', '#e11d48', '#6b7280', '#0ea5e9', '#d946ef'];

export default function RevenueDashboardContent({ isDark }: Props) {
  const { isLoading, error, reportFrom, reportTo, fetchedKey, revenueByStudio, revenueByProvince, revenueByDealType, revenueByPaymentMethod, revenueBySales, revenueByProductType } = useRevenueStore();

  const hasFetched = fetchedKey !== null;

  // Transform studio data to breakdown format
  const studioBreakdown = useMemo(() =>
    revenueByStudio.map(r => ({ name: r.studioName, subLabel: r.provinceName, totalRevenue: r.totalRevenue, orderCount: r.orderCount })),
    [revenueByStudio]
  );

  return (
    <>
      <RevenueDashboardFilters isDark={isDark} />

      {error && (
        <div className={`mt-4 p-4 rounded-lg border ${isDark ? 'bg-red-500/10 border-red-500/30 text-red-300' : 'bg-red-50 border-red-200 text-red-700'}`}>
          {error}
        </div>
      )}

      {isLoading && (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 text-cyan-500 animate-spin" />
        </div>
      )}

      {!isLoading && !hasFetched && (
        <div className="mt-8 flex flex-col items-center justify-center py-20 text-center">
          <Play className={`w-12 h-12 ${isDark ? 'text-slate-600' : 'text-gray-400'} mb-4`} />
          <p className={`text-lg ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Chọn khoảng thời gian và nhấn <span className="text-cyan-400 font-medium">"Chạy report"</span> để xem dữ liệu</p>
        </div>
      )}

      {!isLoading && hasFetched && (
        <div className="mt-4 sm:mt-6 space-y-4 sm:space-y-6">
          <h2 className={`text-xl sm:text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Revenue Dashboard <span className={`text-base font-normal ${isDark ? 'text-cyan-400/80' : 'text-cyan-600'}`}>({reportFrom} — {reportTo})</span>
          </h2>

          {/* Trend — full width */}
          <RevenueTrendChart isDark={isDark} />

          {/* By Studio */}
          <RevenueBreakdown
            isDark={isDark}
            title="Doanh thu theo Studio"
            tooltipText={"Doanh thu từng studio, tính theo PurchaseOrder.\n\nCông thức: SUM(PriceAfterPromotion)\nNhóm theo: Studio (StudioId)\nBộ lọc: PurchaseTime trong kỳ, Deleted = 0"}
            data={studioBreakdown}
            reportFrom={reportFrom}
            reportTo={reportTo}
            csvFileName="revenue_by_studio"
            extraColumn={{ label: 'Tỉnh thành', field: 'subLabel' }}
          />

          {/* By Province */}
          <RevenueBreakdown
            isDark={isDark}
            title="Doanh thu theo Tỉnh thành"
            tooltipText={"Doanh thu nhóm theo tỉnh/thành phố.\n\nCông thức: SUM(PriceAfterPromotion)\nNhóm theo: Provinces.Name (qua Studios.ProvinceId)"}
            data={revenueByProvince}
            reportFrom={reportFrom}
            reportTo={reportTo}
            csvFileName="revenue_by_province"
          />

          {/* By Deal Type + Product Type — 2 cols */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
            <RevenueBreakdown
              isDark={isDark}
              title="Doanh thu theo loại Deal"
              tooltipText={"Phân bổ doanh thu theo loại giao dịch.\n\n• New (10): Khách hàng mới mua gói\n• Upsell (20): Nâng cấp gói hiện tại\n• Renew (30): Gia hạn gói\n\nCông thức: SUM(PriceAfterPromotion)\nNhóm theo: PurchaseOrderDealType"}
              data={revenueByDealType}
              reportFrom={reportFrom}
              reportTo={reportTo}
              csvFileName="revenue_by_deal_type"
              colors={DEAL_TYPE_COLORS}
            />
            <RevenueBreakdown
              isDark={isDark}
              title="Doanh thu theo loại sản phẩm"
              tooltipText={"Doanh thu nhóm theo loại sản phẩm.\n\n• EMS & Pilates: Gói kết hợp EMS + Pilates\n• Combo EMS / Combo Non EMS\n• Group EMS / Group Pilates\n• Hot Yoga, Kick Boxing, Recovery\n• At Home, Retail, Service\n\nCông thức: SUM(PriceAfterPromotion)\nNhóm theo: KindOfProductType"}
              data={revenueByProductType}
              reportFrom={reportFrom}
              reportTo={reportTo}
              csvFileName="revenue_by_product_type"
              colors={PRODUCT_TYPE_COLORS}
            />
          </div>

          {/* By Payment Method — full width */}
          <RevenueBreakdown
            isDark={isDark}
            title="Doanh thu theo phương thức thanh toán"
            tooltipText={"Doanh thu nhóm theo phương thức thanh toán.\n\nCác phương thức: Cash, Transfer, POS, MPOS, Home Credit, Momo, v.v.\n\nCông thức: SUM(PriceAfterPromotion)\nNhóm theo: PaymentMethod"}
            data={revenueByPaymentMethod}
            reportFrom={reportFrom}
            reportTo={reportTo}
            csvFileName="revenue_by_payment"
            colors={PAYMENT_COLORS}
          />

          {/* By Sales — full width */}
          <RevenueBreakdown
            isDark={isDark}
            title="Doanh thu theo nhân viên Sales"
            tooltipText={"Doanh thu theo từng nhân viên sales.\n\nCông thức: SUM(amount)\nNhóm theo: hubspot_owner_id → Owner name"}
            data={revenueBySales}
            reportFrom={reportFrom}
            reportTo={reportTo}
            csvFileName="revenue_by_sales"
            colors={SALES_COLORS}
          />

          {/* Voucher Stats — TODO: debug voucher detection logic */}
          {/* <VoucherStatsCard isDark={isDark} /> */}
        </div>
      )}
    </>
  );
}
